import { useState, useRef } from "react";
import { ENV } from "../../config/env";

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getToken = () => localStorage.getItem("admin_token");

const convertFile = (file, folder, onProgress) =>
  new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${ENV.API_BASE_URL}/admin/media/png-to-gif?folder=${encodeURIComponent(folder)}`);
    xhr.setRequestHeader("Authorization", `Bearer ${getToken()}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      try {
        const res = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300 && res.success) resolve(res.data);
        else reject(new Error(res.message || "Conversion failed"));
      } catch {
        reject(new Error("Invalid server response"));
      }
    };

    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(formData);
  });

const STATUS = { PENDING: "pending", CONVERTING: "converting", DONE: "done", ERROR: "error" };

export default function PngToGifConverter() {
  const [folder, setFolder] = useState("png-to-gif");
  const [queue, setQueue] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const isConverting = useRef(false);

  const enqueueFiles = (selectedFiles) => {
    const items = Array.from(selectedFiles)
      .filter((file) => {
        if (file.type !== "image/png") return false;
        return true;
      })
      .map((file) => ({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        status: STATUS.PENDING,
        progress: 0,
        result: null,
        error: null,
      }));
    setQueue((prev) => [...prev, ...items]);
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.length) enqueueFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) enqueueFiles(e.dataTransfer.files);
  };

  const runQueue = async () => {
    if (isConverting.current) return;
    const pending = queue.filter((i) => i.status === STATUS.PENDING);
    if (pending.length === 0) return;

    isConverting.current = true;

    const updateItem = (id, patch) =>
      setQueue((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));

    for (const item of pending) {
      updateItem(item.id, { status: STATUS.CONVERTING, progress: 0 });
      try {
        const result = await convertFile(item.file, folder.trim() || "png-to-gif", (pct) =>
          updateItem(item.id, { progress: pct })
        );
        updateItem(item.id, { status: STATUS.DONE, progress: 100, result });
      } catch (err) {
        updateItem(item.id, { status: STATUS.ERROR, error: err.message });
      }
    }

    isConverting.current = false;
  };

  return (
    <div className="p-4 sm:p-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-2 text-gray-800">PNG to GIF Converter</h1>
      <p className="text-sm text-gray-500 mb-8">
        Upload one or more PNG images and convert them to GIF using ffmpeg. Files are
        stored in S3 under the folder below.
      </p>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">S3 Folder Name</label>
        <input
          type="text"
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          placeholder="e.g. png-to-gif"
          autoComplete="off"
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 sm:p-10 text-center cursor-pointer transition-colors ${
          isDragging
            ? "border-blue-400 bg-blue-50"
            : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/png"
          className="hidden"
          onChange={handleFileSelect}
        />
        <div className="text-5xl mb-3">🎞️</div>
        <p className="text-gray-700 font-medium text-base">Drop PNG files here or click to browse</p>
        <p className="text-xs text-gray-400 mt-1">PNG only · Max 50 MB per file</p>
      </div>

      {queue.length > 0 && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={runQueue}
            disabled={!queue.some((i) => i.status === STATUS.PENDING)}
            className="rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Convert to GIF
          </button>
        </div>
      )}

      {queue.length > 0 && (
        <div className="mt-6 bg-white rounded-xl border divide-y">
          {queue.map((item) => (
            <div key={item.id} className="px-4 py-3 space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="truncate max-w-xs font-medium text-gray-800" title={item.file.name}>
                  {item.file.name}
                </span>
                <span className="text-xs text-gray-400">{formatBytes(item.file.size)}</span>
              </div>

              {item.status === STATUS.CONVERTING && (
                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-blue-500 h-1.5 transition-all"
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
              )}

              {item.status === STATUS.DONE && item.result && (
                <div className="text-xs text-green-600 flex items-center gap-2">
                  <span>✓ Converted ({formatBytes(item.result.gifSize)})</span>
                  <a
                    href={item.result.gifUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-600"
                  >
                    View GIF
                  </a>
                </div>
              )}

              {item.status === STATUS.ERROR && (
                <p className="text-xs text-red-500">✗ {item.error}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
